"use client";

import { useEffect, useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import ProductCarousel from "@/components/ProductCarousel";
import { getSimilarCategoryProducts } from "@/app/actions/products";
import type { Product } from "@/types";
import { useInitialRelatedProducts } from "@/components/product-detail/RelatedProductsContext";

interface SimilarCategoryProductsProps {
  categoryId: string;
  excludeProductIds?: string[];
  locale: string;
  /** 레이아웃 모드: horizontal(가로 스크롤) 또는 vertical(세로 그리드) */
  layout?: "horizontal" | "vertical";
}

export default function SimilarCategoryProducts({
  categoryId,
  excludeProductIds = [],
  locale,
  layout = "horizontal",
}: SimilarCategoryProductsProps) {
  const t = useTranslations("search");
  // Server-rendered first page (product detail ISR) → real cards and links in
  // the HTML; the mount-time fetch is skipped while it is in use.
  const initialProducts = useInitialRelatedProducts("similar", excludeProductIds);
  const [products, setProducts] = useState<Product[]>(initialProducts ?? []);
  const [isLoading, setIsLoading] = useState(!initialProducts);

  // 배열을 문자열로 변환하여 안정적인 dependency로 사용
  const excludeIdsKey = useMemo(
    () => excludeProductIds.join(","),
    [excludeProductIds]
  );

  const hasInitial = initialProducts !== undefined;

  useEffect(() => {
    if (hasInitial) return;
    async function fetchProducts() {
      setIsLoading(true);
      try {
        const excludeIds = excludeIdsKey ? excludeIdsKey.split(",") : [];
        const data = await getSimilarCategoryProducts({
          categoryId,
          excludeProductIds: excludeIds,
          limit: 6,
          locale,
        });
        setProducts(data);
      } catch (error) {
        console.error("Failed to fetch similar category products:", error);
      } finally {
        setIsLoading(false);
      }
    }

    if (categoryId) {
      fetchProducts();
    } else {
      setIsLoading(false);
    }
  }, [categoryId, excludeIdsKey, locale, hasInitial]);

  return (
    <ProductCarousel
      products={products}
      isLoading={isLoading}
      title={t("recommendations.similarCategory")}
      description={t("recommendations.similarCategoryDescription")}
      layout={layout}
    />
  );
}
